import React, { FC, useMemo, useState } from 'react';

import { Box } from '@src/atoms/Box';
import { useHasScroll } from '@src/hooks/useHasScroll';
import { classnames } from '@src/utils/classnames';
import { Button } from '@src/atoms/Button';
import { FeedbackForm } from '@src/molecules/FeedbackForm';

import { TFilterScheme } from '../../models';
import { FILTER_CONTROLLER_EVENTS, useSyncFilterValue } from '../../useSyncValue';
import { FilterSwipeDialogCommonFilterList } from './_List/FilterSwipeDialogCommonFilterList';
import { FilterSwipeDialogWrapper } from '../_Wrapper/FilterSwipeDialogWrapper';
import { ISwipeDialogComponentProps } from '../models';

import styles from './FilterSwipeDialogCommonFilter.module.scss';

/** Общий компонент для отображение разного рода списков */
export const FilterSwipeDialogCommonFilter: FC<ISwipeDialogComponentProps> = ({
  onClose,
  onSubmit,
  setActiveFilterName,
  resultText = 'Применить',
  controller,
  loading,
  href,
  isDisplayFeedbackForm,
}) => {
  const [isOpenFeedbackForm, setIsOpenFeedbackForm] = useState(false);
  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  const scheme = useSyncFilterValue({ controller, event: FILTER_CONTROLLER_EVENTS.SCHEME });
  const valueList = useSyncFilterValue({ controller, event: FILTER_CONTROLLER_EVENTS.VALUE_LIST });

  const { isScroll } = useHasScroll(ref);

  const handleOpenFeedbackForm = () => {
    setIsOpenFeedbackForm(true);
  };

  const handleCloseFeedbackForm = () => {
    setIsOpenFeedbackForm(false);
  };

  const handleReset = () => {
    controller.onResetList();
  };

  /**
   * firstList - примененные фильтры
   * secondList - остальные, непримененные фильтры
   **/
  const [firstList, secondList] = useMemo(() => {
    const first: TFilterScheme[] = [];
    const second: TFilterScheme[] = [];

    Array.from(scheme).forEach(([key, filter]) => {
      const value = valueList.get(key)?.value;
      const isValue = Array.isArray(value) ? value.length > 0 : !!value;

      if (isValue) {
        first.push(filter);
      } else {
        second.push(filter);
      }
    });
    return [first, second];
  }, [scheme, valueList]);

  const isFirstList = firstList.length > 0;
  const classes = classnames(styles.rootContainer, {
    [styles.rootContainerScroll]: isScroll,
  });

  return (
    <FilterSwipeDialogWrapper
      title="Фильтры"
      //Очень важно чтобы здесь был false, иначе некорректно отображаются тултипы
      isHasInput={false}
      isScroll={isScroll}
      href={href}
      loading={loading}
      resultText={resultText}
      onSubmit={onSubmit}
      rightHeaderBtn={{
        text: 'Закрыть',
        onClick: onClose,
      }}
    >
      <Box column className={classes} ref={setRef}>
        {isFirstList && (
          <FilterSwipeDialogCommonFilterList
            controller={controller}
            title="Выбранные"
            onReset={handleReset}
            items={firstList}
            onOpen={setActiveFilterName}
          />
        )}

        <FilterSwipeDialogCommonFilterList
          title={isFirstList ? 'Остальные' : undefined}
          isMarginTop={!isFirstList}
          items={secondList}
          onOpen={setActiveFilterName}
          controller={controller}
        />

        {isDisplayFeedbackForm && (
          <>
            <Box paddingRight={16} paddingLeft={16} paddingTop={24}>
              <Button
                id="filterFeedbackFormBtn"
                size="md"
                fullWidth
                view="secondaryLight"
                onClick={handleOpenFeedbackForm}
              >
                Нет нужного фильтра?
              </Button>
            </Box>

            {isOpenFeedbackForm && (
              <FeedbackForm
                feedbackFormType="requestProductFilter"
                onClose={handleCloseFeedbackForm}
                importanceLevel="important"
              />
            )}
          </>
        )}
      </Box>
    </FilterSwipeDialogWrapper>
  );
};
